<script setup lang="ts">
import { computed, watch, ref, onUnmounted, onMounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'

import ChipsCount from '@/components/shared/ChipsCount.vue'
import UiAvatar from '@/components/shared/UiAvatar.vue'
import SpineAnimation from '@/components/shared/SpineAnimation.vue'
import SeatCardGroup from '@/components/table/seat/SeatCardGroup.vue'
import SeatPlate from './SeatPlate.vue'

import { useAppMode } from '@/composables/useAppMode'
import { useGameState } from '@/stores/gameState'
import { getAvatarId } from '@/helpers/get-avatar-id'
import { playEvent } from '@/helpers/sound'

import type { SeatClickEvent } from '@/components/table/types'
import type { Seat, HoleCardsValue } from '@acepoker/contract/acepoker'

const PLAYER_TIMEOUT = 12

const props = withDefaults(
  defineProps<{
    index: number
    seat: Seat
    popupPlacement: 'top' | 'bottom' | 'left' | 'right'
    mode?: 'left' | 'right'
    enableTimer?: boolean
    playerCards: HoleCardsValue
    isDealing: boolean
  }>(),
  { mode: 'left' }
)

const emit = defineEmits<{ 'seat-click': [SeatClickEvent] }>()

const { getPlayerById, getActivePlayerState } = useGameState()
const { player: self, roomState, highestBet, isGameStarted, winners } = storeToRefs(useGameState())

const stateBeforeMove = {
  myBet: 0,
  maxBet: 0,
  betsOnTable: 0
}

const player = computed(() => getPlayerById(props.seat.playerId))

const folded = computed(() => {
  return !props.playerCards && roomState.value.hand
})

type Actions = 'fold' | 'call' | 'check' | 'raise' | 'all_in' | 'bet' | 'sb' | 'bb' | 'straddle'

const amIActive = computed(() => props.seat.playerId === roomState.value.hand?.activePlayerId)
const myBet = computed(() => roomState.value.hand?.bets[props.seat.playerId] || 0)

watch(myBet, (newValue) => {
  const blinds = roomState.value.gameConfig?.blinds ?? []
  const betsInPlace = Object.values(roomState.value.hand?.bets ?? {}).filter(Boolean)
  if (
    betsInPlace.length !== blinds.length ||
    betsInPlace.join('/') !== blinds.join('/') ||
    amIActive.value
  )
    return
  if (props.seat.stack === 0) flash('all_in')
})

watch(
  amIActive,
  (newValue, oldValue) => {
    if (!oldValue && newValue) {
      stateBeforeMove.myBet = roomState.value.hand?.bets[props.seat.playerId] || 0
      stateBeforeMove.maxBet = highestBet.value
      stateBeforeMove.betsOnTable = Object.values(roomState.value.hand?.bets ?? {}).filter(
        Boolean
      ).length
    }
    if (!newValue && oldValue) {
      const name = roomState.value.handHistory.at(-1)?.action
      if (!name) return
      name === 'raise' && props.seat.stack === 0 ? flash('all_in') : flash(name)
    }
  },
  { immediate: true }
)

const humanizedNames = {
  sb: 'SB',
  bb: 'BB',
  straddle: 'Straddle',
  fold: 'Fold',
  call: 'Call',
  check: 'Check',
  raise: 'Raise',
  all_in: 'All-in',
  bet: 'Bet',
  '': ''
} as const satisfies Record<Actions | '', string>

const flashAction = ref<'' | Actions>('')
const flash = (message: Actions) => {
  flashAction.value = message
  if (['raise', 'fold', 'check', 'all_in'].includes(message)) playEvent(message)
  else if (['call', 'bet'].includes(message)) playEvent('bet')
  setTimeout(() => {
    flashAction.value = ''
  }, 1000)
}

const showWinner = ref(false)

const seatCardGroupProps = computed(() => {
  const isGameStartedLocal = typeof isGameStarted.value === 'number' || Boolean(isGameStarted.value)

  return {
    cards: props.playerCards,
    isSelf: isGameStartedLocal && props.seat.playerId === self.value?.playerId,
    isOpen: isGameStartedLocal,
    isDealing: props.isDealing
  }
})

watch(
  () => winners.value.findIndex((win) => win === props.seat.playerId),
  (newValue, oldValue) => {
    if (oldValue === -1 && newValue !== -1) showWinner.value = true
  }
)

const avatarId = computed(() => getAvatarId(player.value))
const { mode } = useAppMode()

const timerOn = ref(false)
const timerStyle = ref({})
const activeState = computed(() => getActivePlayerState(props.seat.playerId))
const soundTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

const normalizeTimerDuration = (serverTimeout: number): number => {
  const serverTimeoutSec = serverTimeout / 1000
  if (serverTimeoutSec < PLAYER_TIMEOUT * 0.8) {
    console.warn(`Server timeout too low: ${serverTimeoutSec}s, using default: ${PLAYER_TIMEOUT}s`)
    return PLAYER_TIMEOUT
  }
  return serverTimeoutSec
}

const updateTimerStyle = (
  endTs: number,
  totalSec: number,
  mode: string,
  side: 'left' | 'right'
) => {
  const dist = timerRef.value?.offsetWidth ?? 0
  const remainMs = Math.max(0, endTs - Date.now())
  const frac = remainMs / (totalSec * 1000)
  const sign = mode === 'desktop' ? (side === 'left' ? 1 : -1) : 1
  const remainSec = remainMs / 1000
  const elapsedSec = totalSec - remainSec

  timerStyle.value = {
    '--timer-duration': `${remainSec}s`,
    '--timer-start-scale': `${frac}`,
    '--spark-start-distance': `${sign * dist * frac}px`,
    '--timer-color-delay': `-${elapsedSec}s`,
    'animation-play-state': 'running'
  }
}

watch(
  activeState,
  (newState, oldState) => {
    if (!props.enableTimer) return

    if (newState && !oldState) {
      const savedEndTs = localStorage.getItem(`timer-end-${props.seat.playerId}`)
      const savedDuration = localStorage.getItem(`timer-duration-${props.seat.playerId}`)

      if (savedEndTs && savedDuration) {
        const endTs = parseInt(savedEndTs)
        const totalSec = parseInt(savedDuration)
        const remainMs = Math.max(0, endTs - Date.now())

        if (remainMs > 0) {
          timerOn.value = true
          nextTick(() => updateTimerStyle(endTs, totalSec, mode.value, props.mode!))
          return
        } else {
          localStorage.removeItem(`timer-end-${props.seat.playerId}`)
          localStorage.removeItem(`timer-duration-${props.seat.playerId}`)
        }
      }

      const serverTimeout = newState.activeTimeout ?? PLAYER_TIMEOUT * 1000
      const totalSec = normalizeTimerDuration(serverTimeout)

      const now = Date.now()
      const endTs = now + totalSec * 1000
      localStorage.setItem(`timer-end-${props.seat.playerId}`, endTs.toString())
      localStorage.setItem(`timer-duration-${props.seat.playerId}`, totalSec.toString())

      timerOn.value = true
      nextTick(() => updateTimerStyle(endTs, totalSec, mode.value, props.mode!))
    }

    if (!newState && oldState) {
      timerOn.value = false
      localStorage.removeItem(`timer-end-${props.seat.playerId}`)
      localStorage.removeItem(`timer-duration-${props.seat.playerId}`)
    }
  },
  { immediate: true }
)

const timerRef = ref<HTMLElement | null>(null)

onMounted(() => {
  const savedEndTs = localStorage.getItem(`timer-end-${props.seat.playerId}`)
  const savedDuration = localStorage.getItem(`timer-duration-${props.seat.playerId}`)

  if (savedEndTs && savedDuration && activeState.value) {
    const endTs = parseInt(savedEndTs)
    const totalSec = parseInt(savedDuration)
    const remainMs = Math.max(0, endTs - Date.now())

    if (remainMs > 0) {
      timerOn.value = true
      nextTick(() => updateTimerStyle(endTs, totalSec, mode.value, props.mode!))
    } else {
      localStorage.removeItem(`timer-end-${props.seat.playerId}`)
      localStorage.removeItem(`timer-duration-${props.seat.playerId}`)
    }
  }
})

onUnmounted(() => {
  if (soundTimeout.value) clearTimeout(soundTimeout.value)
})
</script>

<template>
  <seat-plate
    :view-mode="mode"
    :popup-placement="popupPlacement"
    :avatar-placement="props.mode"
    :class="{
      'seat--away': seat.awayTimeout > 0,
      'seat--offline': seat.offline,
      'seat--folded': folded,
      'seat--self': isGameStarted && seat.playerId === self?.playerId,
      [`seat--${props.mode}`]: mode === 'desktop',
      [`seat--${mode}`]: true
    }"
    :data-id="seat.playerId"
    :player-id="player.playerId || 'Player'"
    @click="emit('seat-click', { index: props.index, seat: props.seat })"
  >
    <template #avatar>
      <ui-avatar
        :id="avatarId"
        class="seat__avatar"
        :class="{ 'seat__avatar--active': amIActive }"
      />
      <div v-if="seat.awayTimeout && seat.awayTimeout > 0" class="seat__avatar-away-timer">
        {{ seat.awayTimeout }}
      </div>
    </template>
    <spine-animation
      v-if="showWinner"
      class="seat__winner"
      :class="`seat__winner--${mode}`"
      name="winner"
      animation="play"
      :end-delay="3000"
      :time-scale="0.3"
      @done="showWinner = false"
    />
    <div
      class="seat__plate"
      :class="{
        [`seat__plate--${mode}`]: true,
        [`seat__plate--${props.mode}`]: mode === 'desktop',
        'seat__plate--active': amIActive
      }"
    >
      <div class="seat-plate__shimmer-slide" />
      <SeatCardGroup v-bind="seatCardGroupProps" />
      <div class="seat__name" :class="`seat__name--${mode}`">
        <div class="seat__name__text">{{ player.name }}</div>
      </div>
      <hr class="seat__divider" />
      <span class="seat__chips" :class="`seat__chips--${mode}`" v-if="seat.offline">OFFLINE</span>
      <div v-else class="flex flex-1 items-center justify-between">
        <chips-count class="seat__chips" :class="`seat__chips--${mode}`" :value="seat.stack" />
      </div>
      <div class="seat__timer-container" ref="timerRef">
        <div class="seat__timer" v-if="timerOn" :style="timerStyle">
          <div class="seat__timer-fill"></div>
        </div>
      </div>
      <transition v-if="flashAction" name="seat__action" appear>
        <div class="seat__action" :class="`seat__action--${flashAction} seat__action--${mode}`">
          {{ humanizedNames[flashAction] }}
        </div>
      </transition>
    </div>
  </seat-plate>
</template>

<style lang="scss">
@property --stop1 {
  syntax: '<color>';
  initial-value: #b5d9b0;
  inherits: false;
}

@property --stop2 {
  syntax: '<color>';
  initial-value: #3ab748;
  inherits: false;
}

@property --stop3 {
  syntax: '<color>';
  initial-value: #97c010;
  inherits: false;
}

@keyframes decrease-and-change-color {
  0% {
    --stop1: #b5d9b0;
    --stop2: #3ab748;
    --stop3: #97c010;
  }

  26% {
    --stop1: #d3d9b0;
    --stop2: #9cc643;
    --stop3: #9cc010;
  }

  51% {
    --stop1: #f5ec99;
    --stop2: #d8ad41;
    --stop3: #d3cd29;
  }

  76% {
    --stop1: #f5b599;
    --stop2: #e03636;
    --stop3: #da1a1a;
  }

  100% {
    --stop1: #f5b599;
    --stop2: #e03636;
    --stop3: #da1a1a;
  }
}

@keyframes shimmer-slide {
  0% {
    transform: translateX(-150%);
  }

  100% {
    transform: translateX(150%);
  }
}

@keyframes timer-fill {
  from {
    transform: scaleX(var(--timer-start-scale));
  }

  to {
    transform: scaleX(0);
  }
}

@keyframes spark-move {
  from {
    transform: translateX(var(--spark-start-distance)) translateY(-50%);
  }

  to {
    transform: translateX(0) translateY(-50%);
  }
}

.seat {
  &__avatar {
    width: 100%;
    height: 100%;
    transition: all 0.5s;

    &--active {
      border: solid white 2px;
    }

    &-away-timer {
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      border-radius: 50%;
      font-size: calc(3 * var(--tw));
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    }
  }

  &__plate {
    flex-shrink: 0;
    width: 100%;
    height: 100%;
    border: none !important;
    position: relative;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: calc(1.8 * var(--th));
    font-weight: 500;
    word-wrap: break-word;
    color: #e4e4e4;

    &--desktop {
      left: 0;
      z-index: -2;

      &::before {
        content: '';
        position: absolute;
        background: linear-gradient(180deg, #282930 -1.32%, #1b1c21 99.51%);
        width: 100%;
        height: 100%;
        z-index: -1;
        transform: perspective(10px) rotateX(-1deg);
        border-radius: 7px;
        left: 0;
        top: 0;
        transition: all 0.5s;
      }

      &.seat__plate--active::before {
        border: solid lightblue 2px;
      }
    }

    &--mobile {
      &::before {
        content: '';
        position: absolute;
        background: linear-gradient(180deg, #282930 -1.32%, #1b1c21 99.51%);
        width: 100%;
        height: 100%;
        z-index: -1;
        transform: perspective(10px) rotateX(-2deg);
        border-radius: 7px;
        left: 0;
        top: 0;
        transition: all 0.5s;
      }

      &.seat__plate--active::before {
        border: solid lightblue 2px;
      }
    }

    &--active {
      .seat-plate__shimmer-slide {
        border-radius: 7px;
        inset: 0;
        overflow: hidden;
        position: absolute;

        &:before {
          content: '';
          position: absolute;
          inset: 0;
          width: 150%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          animation: shimmer-slide 2.5s cubic-bezier(0.25, 1, 0.5, 1) infinite;
          transform: translateX(-150%);
        }
      }
    }
  }

  &__name {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px !important;
    font-style: normal;
    font-weight: 500;
    opacity: 0.75;
    transform: translateZ(1px);

    &__text {
      white-space: nowrap;
      overflow: hidden;
      text-align: center;
      text-overflow: ellipsis;
    }

    &--mobile {
      color: #fff;
    }

    &--desktop {
      font-size: inherit !important;
    }
  }

  &__divider {
    margin: 0;
    padding: 0;
    width: 90%;
    height: 0;
    border: none;
    border-bottom: solid #40424c 1px;
    font-size: 0;
    mask-image: linear-gradient(transparent, black 30%, black 70%, transparent 100%);
  }

  &__chips {
    width: 100%;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px !important;
    font-style: normal;
    font-weight: 500;
    white-space: nowrap;

    &--mobile {
      color: #fff;
      font-style: normal;
      font-weight: 600;
      line-height: 100%;
    }

    &--desktop {
      color: #e4e4e4;
      font-size: inherit !important;
    }
  }

  &__timer {
    position: relative;
    width: 100%;
    height: 3px;
    background: #40424c;

    &-fill {
      position: relative;
      width: 100%;
      height: 100%;
      transform-origin: left;
      will-change: transform;
      animation:
        timer-fill linear var(--timer-duration) forwards,
        decrease-and-change-color var(--timer-duration) linear var(--timer-color-delay) forwards;
      background: linear-gradient(90deg, var(--stop1) 0%, var(--stop2) 55%, var(--stop3) 100%);
    }

    &::after {
      content: '';
      position: absolute;
      z-index: 2;
      top: 50%;
      left: calc(100% - 12px);
      width: 24px;
      height: 24px;
      transform-origin: left;
      will-change: transform;
      transform: translateX(var(--spark-start-distance)) translateY(-50%);
      background: url('@/assets/flare.png') center no-repeat;
      animation: spark-move linear var(--timer-duration) forwards;
    }
  }

  &-plate--desktop {
    &.seat-plate--left {
      .seat__plate {
        padding-left: 25%;
      }
    }

    &.seat-plate--right {
      .seat__plate {
        padding-right: 25%;
      }
    }
  }

  &--mobile {
    &.seat--self {
      transform: translate(-50%, -50%) scale(1.1);
      bottom: 0 !important;
    }
  }

  &--away,
  &--offline,
  &--folded {
    .seat__avatar {
      filter: grayscale(100%);
    }
  }

  &--away,
  &--offline {
    .seat__chips {
      opacity: 0.6;
    }
  }

  &__timer-container {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 3px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    display: flex;
    width: 100%;

    @media screen and (max-width: 640px) {
      width: calc(100% - 12%);
      left: 50%;
      transform: translateX(-50%);
    }

    .seat__plate--mobile & {
      padding: 0;

      .seat__timer {
        &::after {
          left: -12px !important;
        }
      }
    }

    .seat__plate--desktop.seat__plate--left & {
      justify-content: flex-start;

      .seat__timer {
        &::after {
          left: -12px;
        }
      }
    }

    .seat__plate--desktop.seat__plate--right & {
      justify-content: flex-end;

      .seat__timer {
        &-fill {
          transform-origin: right;
        }

        &::after {
          left: calc(100% - 12px);
          animation-direction: normal;
        }
      }
    }
  }

  &__cards {
    position: absolute;
    display: flex;
    flex-direction: row;
    width: calc(var(--card-player-width) * 1.5);
    z-index: -2;

    &--mobile {
      z-index: -2;
      transition:
        ease-in-out 0.5s width,
        ease-in-out 0.5s height,
        ease-in-out 0.5s left,
        ease-in-out 0.5s top,
        ease-in-out 0.5s transform;
      top: calc(3px - var(--card-player-height));

      &.seat__cards--open {
        --card-player-width: calc(var(--card-table-width) * 1.03);
        --card-player-height: calc(var(--card-table-height) * 1.03);
        left: 50% !important;
        transform: translateX(-50%);
      }

      .seat-plate--left & {
        left: 0%;
      }

      .seat-plate--right & {
        right: 0%;
      }

      &.seat__cards--self {
        left: auto;
        right: auto;
        --card-player-width: calc(var(--card-table-width) * 1.03);
        --card-player-height: calc(var(--card-table-height) * 1.03);
        z-index: -2;

        .card {
          border: none;
        }
      }
    }

    &--desktop {
      left: 25%;
      top: -110%;
      --card-player-width: calc(var(--card-table-width) * 0.8);
      --card-player-height: calc(var(--card-table-height) * 0.8);

      &.seat__cards--self {
        left: 15%;
        top: -125% !important;
        --card-player-width: calc(var(--card-table-width) * 1.1);
        --card-player-height: calc(var(--card-table-height) * 1.1);
      }
    }

    > *:first-child {
      left: 0;
      transform: rotate(-6deg);
    }

    > *:last-child {
      right: 0;
      transform: rotate(6deg);
    }

    &-enter-active,
    &-leave-active {
      transition: all 0.5s ease-out;
    }

    &-enter-from,
    &-leave-to {
      top: 0 !important;
      opacity: 0.7;
      transform: scale(0.7);
    }
  }

  &__card.card {
    transform-origin: center 100%;
    flex-shrink: 0;
    position: absolute;
    --card-width: var(--card-player-width);
    --card-height: var(--card-player-height);
  }

  &__action {
    position: absolute;
    font-size: calc(1.5 * var(--th));
    left: calc(50% + var(--seat-plate-padding-left) - var(--seat-plate-padding-right));
    top: 97%;
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(2.5 * var(--th));
    border-radius: 3px;
    color: #e4e4e4;
    background: #0a54c1;

    &--desktop {
      width: calc(5 * var(--tw));
      transform: translate(-50%, 0%);
    }

    &--mobile {
      font-size: 9px;
      font-style: normal;
      font-weight: 500;
      width: 50px;
      height: 15px;
      top: 92%;
    }

    &-enter-active,
    &-leave-active {
      transition: all 0.25s ease-out;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
    }

    &--fold {
      background-color: #4d4d5a;
    }

    &--bet,
    &--call,
    &--bb,
    &--sb,
    &--straddle,
    &--check {
      background-color: #0a54c1;
    }

    &--raise {
      background-color: #bd8800;
    }

    &--all_in {
      background-color: #cc4721;
    }
  }

  &__winner {
    width: calc(20 * var(--tw));
    height: calc(20 * var(--th));
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -46%);
    z-index: 1;

    &--mobile {
      height: 120px;
      transform: translate(-50%, -41%);
      width: calc(20 * var(--th));
    }
  }

  &--desktop {
    &.seat--left {
      .seat__winner {
        padding-left: calc(7.84 * var(--tgw));
      }
    }
  }

  &__emoji {
    width: calc(15 * var(--tw));
    height: calc(15 * var(--th));
    bottom: -55%;
    transform: translate(-50%, -50%);
    position: absolute;

    &--desktop {
      &.seat__emoji--right {
        left: 105%;
      }

      &.seat__emoji--left {
        left: -10%;
      }
    }

    &--mobile {
      &.seat__emoji--right {
        left: 28%;
        top: -70%;
      }

      &.seat__emoji--left {
        left: 72%;
        top: -70%;
      }
    }

    &--self {
      left: 0% !important;
      bottom: 0% !important;
      width: calc(30 * var(--tw));
      height: calc(30 * var(--th));
      transform: none;
    }
  }
}
</style>
